import { cache } from "react";

/**
 * Тип настроек, который возвращает gateway `/api/v1/company/general`.
 * Все поля опциональны — admin может оставить любое пустым, в этом случае
 * лендинг падает на захардкоженные дефолты.
 */
export type CompanyHero = {
  title?: string;
  image?: string;
};

export type CompanyService = {
  title: string;
  body: string;
  image?: string;
};

export type CompanyInfoBlock = {
  title?: string;
  body?: string;
};

export type CompanyComfortClass = {
  label: string;
  model: string;
  image?: string;
};

export type CompanyAchievements = {
  title?: string;
  body?: string;
  images?: string[];
};

export type CompanyStat = { value: string; label: string };

export type CompanyAbout = {
  title?: string;
  body?: string;
  stats?: CompanyStat[];
};

export type CompanyPartner = {
  title?: string;
  email?: string;
  telegram_chat_id?: string;
  hashtag?: string;
};

export type CompanyEventArticle = {
  slug: string;
  title: string;
  date: string;
  cardDate?: string;
  image?: string;
  body?: string;
  seo_title?: string;
  seo_description?: string;
  seo_og_image?: string;
};

/**
 * Извлекает сортируемый ключ (год*10000 + месяц*100 + день) из свободной
 * даты мероприятия («5–8 июня 2024», «14 июня — 15 июля 2018», «04–07
 * июня 2026»). Берём первый день, первый месяц и последний год в строке.
 * Если год не найден — 0 (такие уедут в конец).
 */
const RU_MONTHS: Record<string, number> = {
  январ: 1, феврал: 2, март: 3, апрел: 4, мая: 5, май: 5,
  июн: 6, июл: 7, август: 8, сентябр: 9, октябр: 10, ноябр: 11, декабр: 12,
};

export function eventSortKey(a: { date?: string; cardDate?: string }): number {
  const s = `${a.date ?? ""} ${a.cardDate ?? ""}`.toLowerCase();
  const years = s.match(/\d{4}/g);
  const year = years ? parseInt(years[years.length - 1], 10) : 0;
  let month = 0;
  for (const [name, num] of Object.entries(RU_MONTHS)) {
    if (s.includes(name)) { month = num; break; }
  }
  const dayMatch = s.match(/\b(\d{1,2})\b/);
  const day = dayMatch ? parseInt(dayMatch[1], 10) : 0;
  return year * 10000 + month * 100 + day;
}

/** Сортировка статей newest-first (по дате). Не мутирует исходный массив. */
export function sortEventsDesc<T extends { date?: string; cardDate?: string }>(list: T[]): T[] {
  return [...list].sort((a, b) => eventSortKey(b) - eventSortKey(a));
}

/**
 * Унифицированная карточка мероприятия для карусели и сетки /events.
 * Объединяет обычные статьи (events.articles → /events/{slug}) и текущее
 * мероприятие (current_event → /event/{slug}), сортирует newest-first.
 */
export type EventCard = {
  title: string;
  cardDate: string;
  image?: string;
  href: string;
};

export function buildEventCards(settings: {
  events?: CompanyEventsBlock;
  current_event?: CompanyCurrentEvent;
}): EventCard[] {
  const cards: (EventCard & { date?: string })[] = [];

  // Текущее мероприятие (если включено) — ведёт на промо-страницу /event/{slug}.
  const ce = settings.current_event;
  if (ce?.enabled && ce.slug && ce.title) {
    cards.push({
      title: ce.title,
      date: ce.period,
      cardDate: ce.period ?? "",
      image: ce.seo_og_image,
      href: `/event/${ce.slug}`,
    });
  }

  // Обычные статьи мероприятий → детальная /events/{slug}.
  for (const a of settings.events?.articles ?? []) {
    if (!a.slug || !a.title) continue;
    cards.push({
      title: a.title,
      date: a.date,
      cardDate: a.cardDate || a.date,
      image: a.image,
      href: `/events/${a.slug}`,
    });
  }

  return cards
    .sort((x, y) => eventSortKey({ date: y.date, cardDate: y.cardDate }) - eventSortKey({ date: x.date, cardDate: x.cardDate }))
    .map(({ date: _d, ...card }) => card);
}

export type CompanyEventsBlock = {
  title?: string;
  lead?: string;
  seo_title?: string;
  seo_description?: string;
  articles?: CompanyEventArticle[];
};

export type CompanyCurrentEvent = {
  enabled?: boolean;
  show_in_header?: boolean;
  header_label?: string;
  slug?: string;
  title?: string;
  city?: string;
  period?: string;
  subtitle?: string;
  body?: string;
  details?: string;
  application_email?: string;
  application_telegram_chat_id?: string;
  application_hashtag?: string;
  seo_title?: string;
  seo_description?: string;
  seo_og_image?: string;
};

export type CompanyCityCfg = {
  name: string;
  lat: number | string;
  lng: number | string;
  address: string;
  phone: string;
};

export type CompanySocialKind =
  | "facebook"
  | "twitter"
  | "instagram"
  | "vk"
  | "telegram"
  | "whatsapp"
  | "max"
  | "youtube";

export type CompanySocialCfg = {
  kind: CompanySocialKind;
  url: string;
};

export type CompanySettings = {
  logo?: string;
  favicon?: string;
  hero?: CompanyHero;
  services?: CompanyService[];
  info?: CompanyInfoBlock;
  comfort?: CompanyComfortClass[];
  achievements?: CompanyAchievements;
  about?: CompanyAbout;
  partner?: CompanyPartner;
  events?: CompanyEventsBlock;
  current_event?: CompanyCurrentEvent;
  seo?: {
    title?: string;
    description?: string;
    og_image?: string;
  };
  phone?: string;
  email?: string;
  company_info?: string;
  contacts_lead?: string;
  /** Адрес-отправитель для уведомлений (transactional from). */
  notification_email_from?: string;
  /** API-ключ Яндекс.Карт (JS API 2.1). */
  yandex_maps_api_key?: string;
  /** Получатели заявок из модалки «Получить предложение». */
  lead_email?: string;
  lead_telegram_chat_id?: string;
  lead_hashtag?: string;
  hours?: string;
  socials?: CompanySocialCfg[];
  cities?: CompanyCityCfg[];
};

/**
 * GATEWAY_URL — внутренний адрес gateway-сервиса. На локалке dev-машина
 * читает через NEXT_PUBLIC переменную не нужна — все вызовы серверные.
 * В docker compose внутренняя сеть резолвит "gateway:8080".
 */
const GATEWAY_URL =
  process.env.GATEWAY_URL ||
  process.env.NEXT_PUBLIC_GATEWAY_URL ||
  "http://localhost:8081";

/**
 * Кэшированный (на 1 рендер-цикл) загрузчик настроек.
 *
 * - `React.cache` — дедуп внутри одного SSR-рендера: даже если Header,
 *   Footer и SEO вызовут getCompanySettings отдельно, fetch улетит один раз.
 * - `next: { revalidate: 60 }` — ISR: после сохранения в админке новые
 *   значения подхватятся максимум через минуту, без редеплоя.
 *
 * Если gateway недоступен — возвращаем пустой объект. Лендинг не падает,
 * все компоненты должны иметь дефолты.
 */
export const getCompanySettings = cache(async (): Promise<CompanySettings> => {
  // В dev — без кэша (чтобы изменения в админке видны сразу).
  // В prod — ISR 60 секунд: после сохранения новые значения подхватятся
  // максимум через минуту, без редеплоя.
  const fetchOpts: RequestInit =
    process.env.NODE_ENV === "production"
      ? ({ next: { revalidate: 60 } } as RequestInit)
      : { cache: "no-store" };
  try {
    const res = await fetch(`${GATEWAY_URL}/api/v1/company/general`, fetchOpts);
    if (!res.ok) return {};
    const data = (await res.json()) as CompanySettings | null;
    return data ?? {};
  } catch {
    return {};
  }
});

/**
 * Разбивает многострочный текст на отдельные строки, отбрасывает пустые.
 * Используется для title в Hero, company_info / hours в подвале —
 * чтобы admin задавал каждую строку отдельно через перевод строки.
 */
export function splitLines(text: string | undefined): string[] {
  if (!text) return [];
  return text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}
